import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';


type EventSlide ={
  id: number;
  title: string;
  description: string;
  imageUrl: string | null;
}

const EventSlideList: React.FC = () => {
  const [eventSlides, setEventSlides] = useState<EventSlide[]>([]);
  const [formData, setFormData] = useState({ title: '', description: '', imageUrl: '' });
  const [image, setImage] = useState<File | null>(null);
  const [editSlide, setEditSlide] = useState<EventSlide | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchEventSlides = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const res = await fetch(`${API_URL}/api/event-slides`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Failed to fetch event slides');
        const data: EventSlide[] = await res.json();
        setEventSlides(data);
      } catch (err) {
        console.error('Error fetching event slides:', err);
        alert(`Error fetching event slides: ${(err as Error).message || 'Network error'}`);
      }
    };
    fetchEventSlides();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    if (image) {
      formDataToSend.append('image', image);
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/event-slides`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const data = await res.json();
      if (res.ok) {
        alert('Event slide added successfully!');
        setEventSlides([...eventSlides, { id: eventSlides.length + 1, ...formData, imageUrl: data.imageUrl }]);
        setFormData({ title: '', description: '', imageUrl: '' });
        setImage(null);
        setShowAddModal(false);
      } else {
        alert(`Error adding event slide: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding event slide:', err);
      alert(`Error adding event slide: ${(err as Error).message || 'Network error'}`);
    }
  };

  const handleEdit = (slide: EventSlide) => {
    setEditSlide(slide);
    setFormData({
      title: slide.title,
      description: slide.description,
      imageUrl: slide.imageUrl || '',
    });
    setImage(null);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSlide) return;

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('imageUrl', formData.imageUrl);
    if (image) {
      formDataToSend.append('image', image);
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/event-slides/${editSlide.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const data = await res.json();
      if (res.ok) {
        alert('Event slide updated successfully!');
        setEventSlides(
          eventSlides.map((slide) =>
            slide.id === editSlide.id ? { ...slide, ...formData, imageUrl: data.imageUrl || formData.imageUrl } : slide
          )
        );
        setEditSlide(null);
        setFormData({ title: '', description: '', imageUrl: '' });
        setImage(null);
        setShowEditModal(false);
      } else {
        alert(`Error updating event slide: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating event slide:', err);
      alert(`Error updating event slide: ${(err as Error).message || 'Network error'}`);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this event slide?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/event-slides/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (res.ok) {
        alert('Event slide deleted successfully!');
        setEventSlides(eventSlides.filter((slide) => slide.id !== id));
      } else {
        alert(`Error deleting event slide: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting event slide:', err);
      alert(`Error deleting event slide: ${(err as Error).message || 'Network error'}`);
    }
  };

  return (
    <>
      <h2 className="dashboard-title text-center mb-5">Manage Event Slides</h2>
      <div className="text-end mb-3">
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          Add Event Slide
        </Button>
      </div>
      <Table striped bordered hover responsive className="modern-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Description</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {eventSlides.map((slide) => (
            <tr key={slide.id}>
              <td>{slide.id}</td>
              <td>{slide.title}</td>
              <td>{slide.description.substring(0, 50)}...</td>
              <td>
                {slide.imageUrl ? (
                  <Image src={`${API_URL}${slide.imageUrl}`} alt={slide.title} width={50} height={50} rounded />
                ) : (
                  'No Image'
                )}
              </td>
              <td className="action-buttons">
                <Button
                  variant="warning"
                  size="sm"
                  className="me-2 modern-btn"
                  onClick={() => handleEdit(slide)}
                >
                  <i className="fas fa-edit"></i> Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="modern-btn"
                  onClick={() => handleDelete(slide.id)}
                >
                  <i className="fas fa-trash-alt"></i> Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add Event Slide Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Event Slide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter slide title"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter slide description"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control
                type="file"
                name="image"
                accept="image/*"
                onChange={handleFileChange}
              />
            </Form.Group>
            <div className="text-center">
              <Button variant="primary" type="submit" className="me-2 modern-btn">
                <i className="fas fa-check"></i> Add
              </Button>
              <Button variant="secondary" className="modern-btn" onClick={() => setShowAddModal(false)}>
                <i className="fas fa-times"></i> Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Event Slide Modal */}
      {editSlide && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Event Slide</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleEditSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Current Image</Form.Label>
                <div>
                  {formData.imageUrl ? (
                    <Image src={`${API_URL}${formData.imageUrl}`} alt="Current" width={100} height={100} rounded />
                  ) : (
                    'No Image'
                  )}
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Upload New Image (Optional)</Form.Label>
                <Form.Control
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter slide title"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter slide description"
                  required
                />
              </Form.Group>
              <div className="text-center">
                <Button variant="primary" type="submit" className="me-2 modern-btn">
                  <i className="fas fa-check"></i> Update
                </Button>
                <Button variant="secondary" className="modern-btn" onClick={() => setShowEditModal(false)}>
                  <i className="fas fa-times"></i> Cancel
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default EventSlideList;